import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { DEMO_MAX_USERS, addDaysIso, isLicenseExpired, LICENSE_VERTICALS, normalizePrimaryVertical } from './licenseUtils';
import LicenseBranchesEditor from './LicenseBranchesEditor';

const LicenseEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    company_name: '',
    license_key: '',
    license_type: 'standard',
    tier_id: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    start_date: '',
    expiry_date: '',
    max_users: 1,
    status: 'active',
    notes: '',
    primary_vertical: 'distribution',
    is_multi_branch: false,
    branches: [],
  });
  const [activateDates, setActivateDates] = useState({
    start_date: new Date().toISOString().split('T')[0],
    expiry_date: addDaysIso(new Date().toISOString().split('T')[0], 14),
  });
  const [tiers, setTiers] = useState([]);
  const [error, setError] = useState('');
  const [activateInfo, setActivateInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activating, setActivating] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const isDemo = formData.license_type === 'demo';
  const showActivatePanel = isLicenseExpired(formData) || formData.status === 'expired';

  useEffect(() => {
    loadTiers();
  }, []);

  useEffect(() => {
    loadLicense();
  }, [id]);

  const loadTiers = async () => {
    try {
      const response = await api.getLicenseTiers();
      if (response.success) {
        setTiers(response.data || []);
      }
    } catch (err) {
      console.error('Failed to load tiers:', err);
    }
  };

  const loadLicense = async () => {
    try {
      setPageLoading(true);
      const response = await api.getLicenseById(id);
      if (response.success) {
        const license = response.data;
        setFormData({
          company_name: license.company_name || '',
          license_key: license.license_key || '',
          license_type: license.license_type || 'standard',
          tier_id: license.tier_id || '',
          contact_name: license.contact_name || '',
          contact_email: license.contact_email || '',
          contact_phone: license.contact_phone || '',
          start_date: license.start_date ? license.start_date.split('T')[0] : '',
          expiry_date: license.expiry_date ? license.expiry_date.split('T')[0] : '',
          max_users: license.max_users || 1,
          status: license.status || 'active',
          notes: license.notes || '',
          primary_vertical: normalizePrimaryVertical(license.primary_vertical),
          is_multi_branch: Boolean(license.is_multi_branch),
          branches: Array.isArray(license.branches) ? license.branches : [],
        });
        setActivateDates({
          start_date: new Date().toISOString().split('T')[0],
          expiry_date: addDaysIso(new Date().toISOString().split('T')[0], license.license_type === 'demo' ? 14 : 30),
        });
      } else {
        setError('License not found');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setPageLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        ...formData,
        max_users: isDemo ? DEMO_MAX_USERS : Number(formData.max_users),
        primary_vertical: normalizePrimaryVertical(formData.primary_vertical),
        verticals_enabled: [normalizePrimaryVertical(formData.primary_vertical)],
        is_multi_branch: Boolean(formData.is_multi_branch),
        branches: formData.is_multi_branch ? formData.branches : [],
        sync_to_wms: true,
      };
      const response = await api.updateLicense(id, payload);
      if (response.success) {
        navigate('/licenses');
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    setError('');
    setActivating(true);
    setActivateInfo(null);
    try {
      const response = await api.activateLicense(id, activateDates);
      if (response.success) {
        if (response.demo_credentials) {
          setActivateInfo(response.demo_credentials);
        }
        await loadLicense();
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActivating(false);
    }
  };

  if (pageLoading) {
    return <div className="text-center p-4">Loading...</div>;
  }

  return (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setError('')}>&times;</button>
          {error}
        </div>
      )}

      {activateInfo && (
        <div className="alert alert-success">
          <strong>License reactivated.</strong> New admin password: <code>{activateInfo.password}</code>
        </div>
      )}

      {showActivatePanel && (
        <div className="card card-warning mb-3">
          <div className="card-header">
            <h3 className="card-title"><i className="fas fa-redo mr-1" /> Reactivate license</h3>
          </div>
          <div className="card-body">
            <p className="mb-3">
              This license has expired. Choose a new active period, then click <strong>Activate</strong> to restore ChesiWMS login.
            </p>
            <div className="row">
              <div className="col-md-4">
                <label>Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={activateDates.start_date}
                  onChange={(e) => setActivateDates({ ...activateDates, start_date: e.target.value })}
                />
              </div>
              <div className="col-md-4">
                <label>End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={activateDates.expiry_date}
                  onChange={(e) => setActivateDates({ ...activateDates, expiry_date: e.target.value })}
                />
              </div>
              <div className="col-md-4 d-flex align-items-end">
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleActivate}
                  disabled={activating}
                >
                  {activating ? 'Activating…' : 'Activate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                Edit License #{id}
                {isDemo && <span className="badge bg-info ml-2">Demo</span>}
              </h3>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="card-body">
                <div className="form-group">
                  <label>License Tier</label>
                  <select className="form-control" name="tier_id" value={formData.tier_id} onChange={handleChange}>
                    <option value="">No tier</option>
                    {tiers.map((tier) => (
                      <option key={tier.id} value={tier.id}>
                        {tier.name} - {tier.currency} {tier.price_monthly}/month
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Company Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>License Key</label>
                  <input
                    type="text"
                    className="form-control"
                    name="license_key"
                    value={formData.license_key}
                    readOnly
                    style={{ backgroundColor: '#e9ecef' }}
                  />
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Contact Name</label>
                      <input type="text" className="form-control" name="contact_name" value={formData.contact_name} onChange={handleChange} />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Contact Email</label>
                      <input type="email" className="form-control" name="contact_email" value={formData.contact_email} onChange={handleChange} />
                    </div>
                  </div>
                </div>
                <div className="card card-outline card-secondary mb-3">
                  <div className="card-header py-2"><strong>Active period</strong></div>
                  <div className="card-body py-3">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group mb-md-0">
                          <label>Start Date *</label>
                          <input type="date" className="form-control" name="start_date" value={formData.start_date} onChange={handleChange} required />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group mb-0">
                          <label>End Date *</label>
                          <input type="date" className="form-control" name="expiry_date" value={formData.expiry_date} onChange={handleChange} required />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Max Users *</label>
                      <input
                        type="number"
                        className="form-control"
                        name="max_users"
                        value={formData.max_users}
                        onChange={handleChange}
                        min="1"
                        readOnly={isDemo}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Status</label>
                      <select className="form-control" name="status" value={formData.status} onChange={handleChange}>
                        <option value="active">Active</option>
                        <option value="expiring">Expiring</option>
                        <option value="expired">Expired</option>
                        <option value="suspended">Suspended</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label>Business Vertical *</label>
                  <select
                    className="form-control"
                    name="primary_vertical"
                    value={formData.primary_vertical}
                    onChange={handleChange}
                    required
                  >
                    {LICENSE_VERTICALS.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-text text-muted">
                    This client will only see modules for the selected vertical in ChesiWMS.
                  </small>
                </div>
                <LicenseBranchesEditor
                  maxUsers={formData.max_users}
                  isMultiBranch={formData.is_multi_branch}
                  branches={formData.branches}
                  onMultiBranchChange={(checked) =>
                    setFormData((prev) => ({ ...prev, is_multi_branch: checked }))
                  }
                  onBranchesChange={(branches) => setFormData((prev) => ({ ...prev, branches }))}
                />
                <div className="form-group">
                  <label>Notes</label>
                  <textarea className="form-control" name="notes" rows="3" value={formData.notes} onChange={handleChange} />
                </div>
              </div>
              <div className="card-footer">
                <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                  <i className="fas fa-save"></i> {loading ? 'Updating...' : 'Update License'}
                </button>
                <button type="button" className="btn btn-default btn-sm ml-2" onClick={() => navigate('/licenses')}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default LicenseEdit;
